// iter142 — Top-level provider that listens for the `habano:ride-dispatched`
// custom event emitted by WhatsAppActionSheet AFTER a ride WhatsApp link
// is opened. It owns the post-ride UX state machine:
//
//   1. RideWaitingPopup — "Are you waiting for a ride?" with Yes / No.
//      → No: close. → Yes: navigate to /ride/active/:rideId.
//   2. ActiveRide page (separate route at /ride/active/:rideId) shows the
//      running timer + End Ride / SOS / Show My QR buttons.
//
// Purely additive — does NOT modify or replace the existing ride dispatch
// path. The existing WhatsAppActionSheet only ADDS a `dispatchEvent` call.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";

export const RIDE_DISPATCHED_EVENT = "habano:ride-dispatched";

export default function RideTrackingProvider() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [rideId, setRideId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const onDispatched = (e) => {
      const id = e?.detail?.rideId;
      if (!id) return;
      setRideId(id);
      setPopupOpen(true);
    };
    window.addEventListener(RIDE_DISPATCHED_EVENT, onDispatched);
    return () => window.removeEventListener(RIDE_DISPATCHED_EVENT, onDispatched);
  }, []);

  if (!popupOpen) return null;

  const close = () => { setPopupOpen(false); setRideId(null); };
  const goActive = () => {
    setPopupOpen(false);
    if (rideId) navigate(`/ride/active/${rideId}`);
  };

  return (
    <>
      <button type="button" aria-label="Dismiss" data-testid="ride-waiting-scrim"
        onClick={close} className="fixed inset-0 z-[95]" style={{ background: "rgba(0,0,0,0.78)" }} />
      <div data-testid="ride-waiting-popup"
        className="fixed left-1/2 -translate-x-1/2 z-[96]"
        style={{ top: "30%", width: "min(90vw, 340px)",
                 background: "#171717", border: "1px solid rgba(232,184,75,0.35)",
                 borderRadius: 16, padding: "22px 22px 18px",
                 boxShadow: "0 30px 80px rgba(0,0,0,0.85)" }}>
        <button type="button" data-testid="ride-waiting-close" onClick={close}
          aria-label="Close" className="grid place-items-center"
          style={{ position: "absolute", top: 12, right: 14, width: 28, height: 28,
                   borderRadius: "50%", background: "#222", color: "#fff", border: "none" }}>
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
        <h2 className="pr-8"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1.2 }}
          data-testid="ride-waiting-title">
          {lang === "es" ? "¿Estás esperando tu auto?" : "Are you waiting for a ride?"}
        </h2>
        <p className="mt-2"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
          {lang === "es"
            ? "Podemos hacer seguimiento de tu viaje en tiempo real."
            : "We can track your ride in real time."}
        </p>
        <div className="mt-5 flex gap-3">
          <button type="button" data-testid="ride-waiting-no"
            onClick={close}
            className="flex-1"
            style={{ height: 52, background: "transparent", border: "1px solid rgba(232,184,75,0.5)",
                     color: "#E8B84B", borderRadius: 12, fontFamily: "'DM Sans', sans-serif",
                     fontSize: 15, fontWeight: 600 }}>
            {lang === "es" ? "No" : "No"}
          </button>
          <button type="button" data-testid="ride-waiting-yes"
            onClick={goActive}
            className="flex-1 inline-flex items-center justify-center"
            style={{ height: 52, background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                     color: "#0B0B0B", border: "none", borderRadius: 12,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700 }}>
            {lang === "es" ? "Sí" : "Yes"}
          </button>
        </div>
      </div>
    </>
  );
}

export function emitRideDispatched(detail) {
  try {
    window.dispatchEvent(new CustomEvent(RIDE_DISPATCHED_EVENT, { detail }));
  } catch { /* ignore */ }
}

// Re-exported for ActiveRide pages that want a tiny loading shim.
export const RideLoading = () => (
  <div className="grid place-items-center min-h-[40vh]">
    <Loader2 className="w-5 h-5 animate-spin text-foreground/40" />
  </div>
);
